<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from "lodash"
import { computed, ref, onMounted, useTemplateRef, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Spinner from "@/components/ui/Spinner.vue"
import PlaylistService from "@/services/Playlistservice"
import AddPlaylistModal from "@/components/modals/AddPlaylistModal.vue"
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import StreamCard from '@/components/ui/StreamCard.vue'
import PlaylistEmptyPlaceholder from "../../components/Playlist/PlaylistEmptyPlaceholder.vue"
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import ListingSearchEmpty from '@/components/ui/ListingSearchEmpty.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import { usePersistedSort, NAME_SORT } from '@/Utils/usePersistedSort'


const { notify } = useNotification()
const store = useStore()
const router = useRouter()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const addPlaylistModalRef = useTemplateRef('addPlaylistModalRef')

//Refs
const { sortBy, setSort } = usePersistedSort('castr.sort.playlists', NAME_SORT, 'A to Z')
const sortByDropdown = ref(null)
const sortByOptions = NAME_SORT.map((value) => ({ label: value, value }))
const searchQuery = ref('')
const playlistToDelete = ref(null)
// Computed

const loadingStatus = computed(() => store.getters["Ui/loadingStatus"])
const playlist = computed(() => store.getters["Playlist/playlist"])
const showListControls = computed(() => (playlist.value?.length ?? 0) > 2)
const playlistSorted = computed(() => store.getters["Playlist/playlistSorted"])
const playlistFiltered = computed(() => {
  const searchStr = searchQuery.value.toLowerCase();
  return playlistSorted.value?.filter(playlist => playlist?.name?.toLowerCase().includes(searchStr)) || []
})
// The grid is empty because the query missed, not because there are no
// playlists — say so under the (still visible) search box.
const noSearchMatches = computed(() => !!searchQuery.value && playlistFiltered.value.length === 0)

const maxPlaylistCount = computed(() => store.getters['User/maxPlaylistCount'])

const playlistLimit = computed(() => {
  const c = maxPlaylistCount.value
  return typeof c === 'number' && c > 0 ? c : null
})

const playlistHeaderUsage = computed(() => {
  if (!playlist.value?.length) return null
  const meta = {
    used: playlist.value.length,
    label: 'playlists',
    labelSingular: 'playlist',
  }
  if (playlistLimit.value) meta.limit = playlistLimit.value
  return meta
})

const atPlaylistLimit = computed(() => {
  const max = playlistLimit.value
  if (!max) return false
  return (playlist.value?.length ?? 0) >= max
})

const playlistCreateDisabledTitle = computed(() => {
  if (atPlaylistLimit.value) return planLimitReachedMessage('Playlist')
  return ''
})

const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
// Hooks
onMounted(async () => {
  if (!store.getters['Playlist/isLoaded']) {
    store.dispatch('Ui/setLoadingStatus', true)
  } else {
    store.dispatch('Ui/setLoadingStatus', false)
  }
  try {
    store.dispatch('Playlist/setSortOption', sortBy.value)
    await store.dispatch('Playlist/getPlaylist')
    await store.dispatch('Streams/getStreams')
  } finally {
    store.dispatch('Ui/setLoadingStatus', false)
  }
})

// Methods

const createdPlaylist = (playlist) => {
  router.push({ path: '/playlists/' + playlist._id }).catch(() => { })
}

const removePlaylist = async () => {
  if (playlistToDelete.value) {
    const playlistName = playlistToDelete.value.name
    await PlaylistService.deletePlaylist(playlistToDelete.value._id)
    await store.dispatch('Playlist/getPlaylist', true)
    notify({
      group: 'success',
      text: `Playlist "${playlistName}" is completely deleted from your account`,
    })

  } else {
    notify({ group: 'error', text: `Playlist not found` })

  }
}
const showAddPlaylistModal = () => {
  addPlaylistModalRef.value?.toggleModal()
}

const toggleStream = async (playlist) => {
  await PlaylistService.playlistToggle(playlist._id, !playlist.disabled)
  await store.dispatch('Playlist/getPlaylist', true)
  notify({ group: 'success', text: `Playlist "${playlist.name}" is ${playlist.disabled ? 'Enabled' : 'Disabled'}` })
}

const showDeletePlaylistModal = (playlist) => {
  playlistToDelete.value = playlist
  showModal('delete-playlist')
}

const sortPlaylist = (option) => {
  setSort(option)
  store.dispatch('Playlist/setSortOption', option)
  sortByDropdown.value?.toggleDropdown()
}

</script>

<template>
  <div class="container detail-shell px-4">
    <PageHeader
      title="Playlists"
      subtitle="Curate live streams and videos into a shareable sequence."
      mobile-inline
      :usage="playlistHeaderUsage"
    >
      <Tooltip :active="atPlaylistLimit" wrap-trigger :html="true" :hide="1000">
        <FormButton class="gap-1" size="md" :disabled="atPlaylistLimit" @click="showAddPlaylistModal">
          <icon-base class="" classIcon="mr-2">
            <icon-plus />
          </icon-base>
          New <span class="hidden md:block">playlist</span>
        </FormButton>
        <template #content>
          <span class="text-surface-8 text-sm" v-html="playlistCreateDisabledTitle" />
        </template>
      </Tooltip>
    </PageHeader>
    <Spinner v-if="loadingStatus" text="Loading your playlists…" classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <main v-else-if="playlist?.length === 0">
      <PlaylistEmptyPlaceholder @showAddPlaylistModal="showAddPlaylistModal" />
    </main>
    <main v-else>
      <ResponsiveToolbar class="mt-4 md:mt-0 !mb-4">
        <template v-if="showListControls">
        <DropDownMenu type="secondary" ref="sortByDropdown" position="left">
          <template #toggle-button>
            <span class="text-surface-8">Sort by:&nbsp;</span>
            <span class="text-white">{{ sortBy }}</span>
            <icon-base class="!ml-1 shrink-0 text-surface-8">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem v-for="option in sortByOptions" :key="option.value" :active="sortBy === option.value" @click="sortPlaylist(option.value)">
              {{ option.label }}
              <icon-base v-if="sortBy === option.value" class="shrink-0 !ml-2 text-surface-7" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
        <ListingSearchInput v-model="searchQuery" />
        </template>
        <ViewModeSwitcher class="shrink-0" />
      </ResponsiveToolbar>
      <ul class="grid auto-rows-fr"
        :class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'">
        <li v-for="playlist in playlistFiltered" :key="playlist._id">
          <StreamCard :stream="playlist" card-type="playlist" :compact-mode="shouldShowCompactModeStreams"
            @delete-stream="showDeletePlaylistModal(playlist)"
            @toggle-stream="toggleStream(playlist)" height="sm" />
        </li>
        <li v-if="!searchQuery">
          <ListingCreateTile
            label="playlist"
            :view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
            :used="playlist?.length ?? 0"
            :limit="playlistLimit"
            :limit-reached="atPlaylistLimit"
            @create="showAddPlaylistModal"
          />
        </li>
      </ul>
      <ListingSearchEmpty v-if="noSearchMatches" :query="searchQuery" noun="playlists" @clear="searchQuery = ''" />
    </main>
    <add-playlist-modal ref="addPlaylistModalRef" @playlist-created="createdPlaylist" :playlist="playlist" />
    <demo-video-modal videoid="tDgMUJ40LkS3jrnJeMTQc" />
    <confirm-modal modalId="delete-playlist" message="Are you sure you want to delete the playlist?" modal-type="danger"
      @modal-confirm="removePlaylist(playlistToDelete)" />
  </div>
</template>

<style scoped></style>